"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * @class Select
 * @description select element connected to form and field, also usable standalone
 */
var classNames = require("classnames");
var React = require("react");
var InputHelper_1 = require("./InputHelper");
var useEscPropagationHandler_1 = require("../../../hooks/useEscPropagationHandler");
var util_1 = require("../../../utils/util");
var Actionable_1 = require("@oui/core/Actionable");
// We can't specify 'selected' at the option level or react complains.
// Instead, use a guid to represent the value of a placeholder option so that
// we can set the value at the select level.
var placeholderGuid = "c5220728-d269-82f7-0acc-811a5d97b39e";
// TODO: test this (especially that it doesn't keep adding the placeholder if it's already there)
// once the PR that adds coverage is merged
function makeOptions(_a) {
    var _b = _a.options, options = _b === void 0 ? [] : _b, placeholder = _a.placeholder;
    // if there's a placeholder and it hasn't already been added to to the options, add a dummy option at the beginning
    var maybePlaceholder = placeholder && !(options.length > 0 && options[0].label === placeholder)
        ? [
            React.createElement("option", { key: placeholder, value: placeholderGuid, disabled: true }, placeholder)
        ]
        : [];
    return maybePlaceholder.concat(options.map(function (option, index) {
        if (option.options) {
            return (React.createElement("optgroup", { key: index, label: option.label }, makeOptions({ options: option.options })));
        }
        return (React.createElement("option", { key: index, value: option.value, disabled: option.disabled, title: option.title }, option.label));
    }));
}
var findOptionRecursive = function (options, predicate) {
    var e_1, _a;
    try {
        for (var options_1 = __values(options), options_1_1 = options_1.next(); !options_1_1.done; options_1_1 = options_1.next()) {
            var option = options_1_1.value;
            if (predicate(option)) {
                return option;
            }
            var childMatch = option.options && findOptionRecursive(option.options, predicate);
            if (childMatch) {
                return childMatch;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (options_1_1 && !options_1_1.done && (_a = options_1.return)) _a.call(options_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
};
exports.findOption = function (options, value) {
    return findOptionRecursive(options, function (option) { return option.value === value; });
};
exports.findFirstValue = function (options) {
    var firstOption = findOptionRecursive(options, function (option) { return option.value !== undefined; });
    return firstOption && firstOption.value;
};
var makeSyntheticChangeEvent = function (element, value) {
    var target = element;
    target.value = value;
    var event = util_1.createNewEvent("change");
    var syntheticEvent = {
        target: target,
        currentTarget: target,
        nativeEvent: event,
        bubbles: false,
        cancelable: false,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        preventDefault: function () { },
        isDefaultPrevented: function () { return false; },
        stopPropagation: function () { },
        isPropagationStopped: function () { return false; },
        persist: function () { },
        timeStamp: Date.now(),
        type: "change"
    };
    return syntheticEvent;
};
/**
 * OUI select component.
 *
 * @oui-component Select
 */
var Select = function (_a) {
    var label = _a.label, value = _a.value, defaultValue = _a.defaultValue, labelledBy = _a.labelledBy, options = _a.options, placeholder = _a.placeholder, fieldName = _a.fieldName, onChange = _a.onChange, testId = _a.testId, disabled = _a.disabled, focusable = _a.focusable, ariaBusy = _a.ariaBusy, busy = _a.busy;
    return (React.createElement(InputHelper_1.default, { disabled: disabled, value: value, defaultValue: defaultValue, fieldName: fieldName, onChange: onChange, mapFormValue: InputHelper_1.mapEventTargetValue }, function (helper) {
        var isPlaceholder = helper.value === undefined && placeholder !== undefined;
        var className = classNames("oui-select", "oui-react-select", {
            "oui-text-muted": isPlaceholder
        });
        var element = React.useRef(undefined);
        var describedBy = [helper.hintId, helper.errorId].join(" ").trim();
        // FireFox and IE11 bleed ESC event when closed by keypress, need to prevent bubbling by default.
        // no open/close API to tie to, so an additional ESC will be needed to close modal when focus in Select
        useEscPropagationHandler_1.default(element, true);
        // If there is no value, and no placeholder fire an onChange to the first option with a value
        // so that the form state reflects reality.
        React.useEffect(function () {
            if (helper.value === undefined && placeholder === undefined) {
                var enabledOptions = options.filter(function (o) { return !o.disabled; });
                if (enabledOptions.length) {
                    var newValue = exports.findFirstValue(enabledOptions);
                    var event_1 = makeSyntheticChangeEvent(element.current, newValue && String(newValue));
                    helper.onChange(event_1);
                }
            }
        }, [helper.value, placeholder === undefined]);
        var busyProp = busy || ariaBusy;
        var value = helper.value !== undefined
            ? helper.value
            : (isPlaceholder && placeholderGuid) || "";
        var props = __assign({ className: className,
            disabled: disabled,
            focusable: focusable,
            value: value, from: "select", ref: element, id: helper.fieldId, name: helper.fieldName, onChange: helper.onChange, "data-test-id": testId }, util_1.ariaProps({ label: label, describedBy: describedBy, labelledBy: labelledBy, busy: busyProp }));
        var handleMouseDown = React.useCallback(function (e) {
            if (disabled) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        }, [disabled]);
        React.useEffect(function () {
            var current = element.current;
            if (!current)
                return;
            current.addEventListener("mousedown", handleMouseDown);
            return function () { return current.removeEventListener("mousedown", handleMouseDown); };
        }, [element.current, handleMouseDown]);
        return (React.createElement(Actionable_1.default, __assign({}, props), makeOptions({ placeholder: placeholder, options: options })));
    }));
};
exports.default = Select;
//# sourceMappingURL=Select.js.map